import {
	Box,
	Chip,
	Container,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { useNavigate } from "react-router-dom";
import { useCollectionGlobal } from "../hooks/useFirestoreCollection";
import { and, where, doc, onSnapshot, Timestamp } from "firebase/firestore";
import dayjs from "dayjs";
import { db } from "../firebase";
import { useEffect, useState, useRef, useCallback } from "react";

interface CardRead {
	timestamp: Timestamp;
	cardIdDecimal: string;
	success: boolean;
	cardHolderName: string | null;
	profileName: string | null;
	profileUid: string | null;
}

// Audio instances for card read sounds
const successSound = new Audio("/static/positive2.mp3");
const failSound = new Audio("/static/negative.mp3");

export const TicketControl: React.FC = () => {
	const navigate = useNavigate();
	const { docs: tickets } = useCollectionGlobal(
		"dayTickets",
		and(
			where("created", ">", dayjs().startOf("day").toDate()),
			where("created", "<", dayjs().endOf("day").toDate())
		)
	);

	const [cardReads, setCardReads] = useState<CardRead[]>([]);
	const [audioEnabled, setAudioEnabled] = useState(false);
	const lastReadCountRef = useRef<number>(0);
	const isFirstLoadRef = useRef<boolean>(true);

	// Unlock audio playback on user interaction
	const enableAudio = useCallback(() => {
		// Play both sounds at zero volume to unlock them
		successSound.volume = 0;
		failSound.volume = 0;
		successSound
			.play()
			.then(() => {
				successSound.pause();
				successSound.currentTime = 0;
				successSound.volume = 1;
			})
			.catch(() => {});
		failSound
			.play()
			.then(() => {
				failSound.pause();
				failSound.currentTime = 0;
				failSound.volume = 1;
			})
			.catch(() => {});
		setAudioEnabled(true);
	}, []);

	// Play sound for new card reads
	const playSound = useCallback(
		(success: boolean) => {
			if (!audioEnabled) return;
			const audio = success ? successSound : failSound;
			audio.currentTime = 0;
			audio.play().catch((err) => console.log("Audio play failed:", err));
		},
		[audioEnabled]
	);

	useEffect(() => {
		const todayString = dayjs().format("YYYY-MM-DD");
		const docRef = doc(db, "cardReads", todayString);

		const unsubscribe = onSnapshot(docRef, (docSnap) => {
			const data = docSnap.data();
			if (data?.reads) {
				const reads = data.reads as CardRead[];
				// Sort by timestamp descending (newest first)
				reads.sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);

				// Play sound for new reads (not on first load)
				if (
					!isFirstLoadRef.current &&
					reads.length > lastReadCountRef.current
				) {
					// Get the newest read and play appropriate sound
					const newestRead = reads[0];
					playSound(newestRead.success);
				}

				lastReadCountRef.current = reads.length;
				isFirstLoadRef.current = false;
				setCardReads(reads);
			}
		});

		return () => unsubscribe();
	}, [playSound]);

	return (
		<Container>
			<Box pt={2}>
				<Box display="flex" justifyContent={"end"}>
					<IconButton onClick={() => navigate("/rosterToday")}>
						<CloseIcon />
					</IconButton>
				</Box>

				<Stack spacing={3}>
					{/* Card Reads Section */}
					<Box>
						<Box display="flex" alignItems="center" gap={1}>
							<Typography variant="h5">Kortkontroll</Typography>
							<Tooltip
								title={
									audioEnabled
										? "Lyd aktivert"
										: "Klikk for å aktivere lyd"
								}
							>
								<IconButton
									onClick={enableAudio}
									size="small"
									sx={{
										color: audioEnabled
											? "success.main"
											: "text.disabled",
									}}
								>
									{audioEnabled ? (
										<VolumeUpIcon />
									) : (
										<VolumeOffIcon />
									)}
								</IconButton>
							</Tooltip>
						</Box>
						<Divider sx={{ mb: 1 }} />
						{cardReads.length === 0 ? (
							<Typography color="text.secondary" py={2}>
								Ingen kort skannet i dag ennå...
							</Typography>
						) : (
							<List dense>
								{cardReads.map((read, index) => (
									<ListItem
										key={`${read.cardIdDecimal}-${read.timestamp.seconds}-${index}`}
										sx={{
											bgcolor: read.success
												? "success.light"
												: "error.light",
											borderRadius: 1,
											mb: 0.5,
											opacity: index === 0 ? 1 : 0.8,
										}}
									>
										<ListItemIcon>
											{read.success ? (
												<CheckCircleIcon color="success" />
											) : (
												<CancelIcon color="error" />
											)}
										</ListItemIcon>
										<ListItemText
											primary={
												read.success ? (
													<>
														<strong>
															{
																read.cardHolderName
															}
														</strong>
														{read.profileName && (
															<Typography
																component="span"
																variant="body2"
																color="text.secondary"
																ml={1}
															>
																(
																{
																	read.profileName
																}
																)
															</Typography>
														)}
													</>
												) : (
													<>
														<strong>
															Ukjent kort
														</strong>
														<Typography
															component="span"
															variant="body2"
															color="text.secondary"
															ml={1}
														>
															(
															{read.cardIdDecimal}
															)
														</Typography>
													</>
												)
											}
											secondary={dayjs(
												read.timestamp.toDate()
											).format("HH:mm:ss")}
										/>
										<Chip
											label={
												read.success ? "OK" : "Avvist"
											}
											color={
												read.success
													? "success"
													: "error"
											}
											size="small"
										/>
									</ListItem>
								))}
							</List>
						)}
					</Box>

					{/* Day Tickets Section */}
					<Box>
						<Typography variant="h5">Dagens billetter</Typography>
						<Divider />
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Kodeord</TableCell>
									<TableCell>tlf</TableCell>
									<TableCell align="center">Antall</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{tickets.map((ticket: any) => {
									return (
										<TableRow key={ticket.id}>
											<TableCell>
												{ticket.codeword}
											</TableCell>
											<TableCell>
												****
												{ticket.mobileNumber.slice(4)}
											</TableCell>
											<TableCell align="center">
												{ticket.numTickets}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Box>
				</Stack>
			</Box>
		</Container>
	);
};
